import * as React from "react";
import { useEffect } from "react";
import { Admin, Resource, CustomRoutes, AppBar, TitlePortal, Layout } from "react-admin";
import { Route } from "react-router-dom";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PaymentsIcon from "@mui/icons-material/Payments";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { customDataProvider } from "./customDataProvider";
import { authProvider, storeIdentityFromToken } from "./authProvider";
import { LoginPage } from "./LoginPage";
import { ColaboratorCreate, ColaboratorEdit, ColaboratorList } from "./colaboratori";
import { EchipeTree } from "./EchipeTree";
import { EchipaCreate, EchipaEdit } from "./echipe";
import { EvenimenteCalendar } from "./EvenimenteCalendar";
import { RaportOre } from "./RaportOre";
import { AvansList, AvansCreate, AvansEdit } from "./avansuri";
import { i18nProvider } from "./i18nProvider";
import { ProfilPage } from "./ProfilPage";
import { PrivacyPage } from "./PrivacyPage";

const TokenGuard = () => {
    useEffect(() => {
        const check = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (payload.exp < Math.floor(Date.now() / 1000)) {
                    // Expirat - incercam refresh silent
                    const refreshToken = localStorage.getItem('refresh_token');
                    if (!refreshToken) { window.location.reload(); return; }

                    const r = await fetch(
                        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/token/refresh`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ refresh_token: refreshToken }),
                        }
                    );
                    if (r.ok) {
                        const { token: newToken, refresh_token: newRefresh } = await r.json();
                        localStorage.setItem('token', newToken);
                        if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
                        // rescriem si rolurile/tenantul, nu doar token-ul
                        storeIdentityFromToken(newToken, localStorage.getItem('username'));
                    } else {
                        ['token', 'refresh_token', 'roles', 'username', 'tenant_id', 'tenant_slug', 'is_super_admin', 'colaborator_id']
                            .forEach(k => localStorage.removeItem(k));
                        window.location.reload();
                    }
                }
            } catch (e) {
                localStorage.removeItem('token');
                window.location.reload();
            }
        };
        check();
        const interval = setInterval(check, 60000);
        return () => clearInterval(interval);
    }, []);
    return null;
};

const ProfilButton = () => (
    <Tooltip title="Profilul meu">
        <IconButton color="inherit" href="#/profil" size="large">
            <AccountCircleIcon />
        </IconButton>
    </Tooltip>
);

const MyAppBar = () => (
    <AppBar>
        <TokenGuard />
        <TitlePortal />
        <ProfilButton />
    </AppBar>
);

const MyLayout = (props) => <Layout {...props} appBar={MyAppBar} />;

const App = () => (
    <Admin
        dataProvider={customDataProvider}
        authProvider={authProvider}
        i18nProvider={i18nProvider}
        loginPage={LoginPage}
        layout={MyLayout}
        requireAuth
    >
        <Resource
            name="evenimente"
            list={EvenimenteCalendar}
            icon={CalendarMonthIcon}
            options={{ label: "Calendar" }}
        />
        <Resource
            name="rapoarte-ore"
            list={RaportOre}
            icon={AssessmentIcon}
            options={{ label: "Raport ore" }}
        />
        <Resource
            name="avansuri"
            list={AvansList}
            create={AvansCreate}
            edit={AvansEdit}
            icon={PaymentsIcon}
            options={{ label: "Avansuri" }}
        />
        <Resource
            name="echipe"
            list={EchipeTree}
            create={EchipaCreate}
            edit={EchipaEdit}
            icon={AccountTreeIcon}
            options={{ label: "Echipe" }}
        />
        <Resource
            name="colaboratori"
            list={ColaboratorList}
            edit={ColaboratorEdit}
            create={ColaboratorCreate}
            icon={GroupsIcon}
            options={{ label: "Colaboratori" }}
        />
        <CustomRoutes noLayout>
            <Route path="/privacy" element={<PrivacyPage />} />
        </CustomRoutes>
        <CustomRoutes>
            <Route path="/profil" element={<ProfilPage />} />
        </CustomRoutes>
    </Admin>
);

export default App;
